import React, { FC, useEffect } from "react";
import { LuUserPlus, LuX } from "react-icons/lu";

import { seatConfirmCopy, SeatPreview } from "./subscription.utils";

/**
 * "Add a seat for {name}?" before an invitation is sent (owner-approved
 * mockup 2026-10-24). With "Manage subscription & payment": plan, seats
 * N -> N+1, new total, what is charged and the card. Without: no prices.
 * The seat itself starts when the person accepts (server).
 */
export const AddSeatConfirmModal: FC<{
  preview: SeatPreview;
  name: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ preview, name, busy, onCancel, onConfirm }) => {
  const copy = seatConfirmCopy(preview, name);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  return (
    <div className={"modal modal-open font-Manrope"}>
      <div
        role={"dialog"}
        aria-modal={"true"}
        aria-label={copy.title}
        className={
          "modal-box w-[480px] max-w-[calc(100vw-32px)] rounded-[16px] p-0 shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
        }>
        <div
          className={
            "relative flex items-center gap-3 border-b border-[#EDEFF3] px-6 py-5"
          }>
          <span
            className={
              "flex h-9 w-9 items-center justify-center rounded-full bg-[#F3F0FF] text-[#6B4EFF]"
            }>
            <LuUserPlus size={18} />
          </span>
          <div className={"text-[17px] font-extrabold text-[#1F2433]"}>
            {copy.title}
          </div>
          <button
            type={"button"}
            aria-label={"Close"}
            onClick={onCancel}
            disabled={busy}
            className={
              "absolute right-6 text-[#B4B9C6] transition-colors hover:text-[#1F2433]"
            }>
            <LuX size={18} />
          </button>
        </div>
        <div className={"px-6 py-5"}>
          {copy.lines.length > 0 && (
            <dl
              className={
                "mb-4 divide-y divide-[#EDEFF3] rounded-[14px] border border-[#EDEFF3] bg-[#F8F9FB] px-4"
              }>
              {copy.lines.map((line) => (
                <div
                  key={line.label}
                  className={"flex justify-between gap-4 py-3 text-[13.5px]"}>
                  <dt className={"shrink-0 font-semibold text-[#6B7185]"}>
                    {line.label}
                  </dt>
                  <dd className={"text-right font-bold text-[#1F2433]"}>
                    {line.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          <p className={"text-[13.5px] leading-relaxed text-[#4A5168]"}>
            {copy.note}
          </p>
        </div>
        <div
          className={
            "flex items-center justify-end gap-2.5 border-t border-[#EDEFF3] px-6 py-4"
          }>
          <button
            type={"button"}
            onClick={onCancel}
            disabled={busy}
            className={
              "h-[38px] rounded-lg border border-[#DCE2EA] bg-white px-[22px] text-sm font-bold text-[#3A4256] hover:bg-[#F8F9FB]"
            }>
            Cancel
          </button>
          <button
            type={"button"}
            onClick={onConfirm}
            disabled={busy}
            className={
              "h-[38px] rounded-lg bg-[#6B4EFF] px-6 text-sm font-bold text-white shadow-[0_4px_14px_rgba(107,78,255,0.28)] hover:bg-[#5A3EEB] disabled:bg-[#C3C8D4] disabled:shadow-none"
            }>
            {busy ? "Sending…" : copy.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
